import React, { ReactNode } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link, useLocation, useNavigate } from '../../router/Router';
import {
  IconCreditCard,
  IconFolder,
  IconLink,
  IconSettings,
  IconShield,
  IconSync,
} from '../ui/Icons';

const NAV_ITEMS = [
  { to: '/projects', label: 'Projects & Pipes', icon: IconFolder },
  { to: '/connections', label: 'Connections', icon: IconLink },
  { to: '/billing', label: 'Billing & Licenses', icon: IconCreditCard },
  { to: '/settings/user', label: 'Settings & Team', icon: IconSettings },
];

export function ClientLayout({ children }: { children: ReactNode }) {
  const {
    portalMode,
    user,
    company,
    impersonation,
    maintenanceMode,
    stopImpersonation,
    logoutClient,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleExitImpersonation = async () => {
    await stopImpersonation();
    if (portalMode !== 'client') {
      navigate('/admin/users');
    }
  };

  const handleLogout = async () => {
    await logoutClient();
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 text-slate-900">
      {/* Impersonation Active Banner */}
      {impersonation.active && (
        <aside
          aria-label="Impersonation banner"
          className="bg-amber-500 text-slate-950 px-4 py-2 text-xs font-medium flex items-center justify-between gap-4 shadow-sm"
        >
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center rounded bg-slate-950/15 px-2 py-0.5 font-semibold uppercase tracking-wider">
              Impersonation Mode
            </span>
            <span>
              Viewing Client Portal as{' '}
              <strong>{impersonation.label || user?.email || 'Customer User'}</strong>
              {company ? ` (${company.name})` : ''}
            </span>
          </div>
          <button
            type="button"
            onClick={handleExitImpersonation}
            className="rounded-lg bg-slate-900 px-3 py-1 text-xs font-semibold text-white hover:bg-slate-800 transition-colors"
          >
            End Impersonation &amp; Return to Admin
          </button>
        </aside>
      )}

      {/* Global Maintenance Banner */}
      {maintenanceMode && (
        <aside
          aria-label="Maintenance mode notice"
          className="bg-rose-600 text-white px-4 py-2 text-xs font-medium flex items-center justify-between gap-4"
        >
          <div className="flex items-center gap-2">
            <span className="font-bold uppercase tracking-wider">Maintenance Mode Active:</span>
            <span>
              Scheduled synchronization workers are currently paused by platform administrators.
            </span>
          </div>
          {portalMode !== 'client' && (
            <Link
              to="/admin/maintenance"
              className="underline font-semibold hover:text-rose-100 whitespace-nowrap"
            >
              Manage in Admin Console →
            </Link>
          )}
        </aside>
      )}

      {/* Primary Top Navigation Header */}
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8 h-16">
          <div className="flex items-center gap-6">
            <Link to="/projects" className="flex items-center gap-2.5 group">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-sm group-hover:bg-indigo-700 transition-colors">
                <IconSync className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-base font-bold tracking-tight text-slate-900">Sync</span>
                  {impersonation.active && (
                    <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-indigo-700 border border-indigo-200">
                      Client Portal
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-slate-500 hidden sm:block">
                  Procore ↔ Smartsheet
                </p>
              </div>
            </Link>

            <nav aria-label="Client portal navigation" className="hidden md:flex items-center gap-1">
              {NAV_ITEMS.map((item) => {
                const Icon = item.icon;
                const active =
                  location.pathname === item.to ||
                  (item.to === '/projects' && location.pathname.startsWith('/projects')) ||
                  (item.to === '/connections' && location.pathname.startsWith('/connection')) ||
                  (item.to === '/billing' &&
                    (location.pathname.startsWith('/billing') ||
                      location.pathname.startsWith('/license'))) ||
                  (item.to === '/settings/user' && location.pathname.startsWith('/settings'));

                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      active
                        ? 'bg-indigo-50 text-indigo-700'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            {company && (
              <Link
                to="/auth/procore-company"
                title="Switch Procore Company"
                className="hidden lg:flex flex-col items-end text-right hover:opacity-80 transition-opacity"
              >
                <span className="text-xs font-semibold text-slate-800">{company.name}</span>
                <span className="text-[11px] text-slate-500 font-mono">
                  Procore #{company.procore_company_id}
                </span>
              </Link>
            )}

            {user && (
              <div className="hidden sm:flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1">
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                <span className="text-xs font-medium text-slate-700 max-w-[160px] truncate">
                  {user.full_name || user.email}
                </span>
              </div>
            )}

            {portalMode === 'all' && (
              <Link
                to="/admin/companies"
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                title="Switch to Admin Console"
              >
                <IconShield className="w-3.5 h-3.5 text-indigo-600" />
                <span>Admin Console</span>
              </Link>
            )}

            <button
              type="button"
              onClick={handleLogout}
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>

        {/* Mobile Secondary Navigation */}
        <nav
          aria-label="Mobile client navigation"
          className="flex md:hidden items-center gap-1 overflow-x-auto border-t border-slate-100 px-4 py-2"
        >
          {NAV_ITEMS.map((item) => {
            const active =
              location.pathname === item.to ||
              (item.to === '/projects' && location.pathname.startsWith('/projects')) ||
              (item.to === '/connections' && location.pathname.startsWith('/connection')) ||
              (item.to === '/settings/user' && location.pathname.startsWith('/settings'));
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium ${
                  active ? 'bg-indigo-50 text-indigo-700' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>
      </header>

      {/* Main Content Landmark */}
      <main className="mx-auto w-full max-w-7xl flex-1 px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      <footer className="border-t border-slate-200 bg-white py-4 px-4 sm:px-6 lg:px-8 text-xs text-slate-500">
        <div className="mx-auto max-w-7xl flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span>Sync Construction Schedule &amp; Task Integration Platform</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/connections" className="hover:text-slate-800">
              OAuth Connections
            </Link>
            <Link to="/billing" className="hover:text-slate-800">
              Subscription &amp; Invoices
            </Link>
            <Link to="/settings/user" className="hover:text-slate-800">
              Preferences
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
